/**
 * Notification Sound Utilities
 * Handles playing different sounds for various notification types
 */

// Import audio files
import chatSound1 from '../assets/audio/chat/1.wav';
import chatSound2 from '../assets/audio/chat/2.wav';
import whistleSound1 from '../assets/audio/FootballRefereeWhistle1.mp3';
import whistleSound2 from '../assets/audio/FootballRefereeWhistle2.mp3';



// Sound mapping for different notification types
const NOTIFICATION_SOUNDS = {
  message: chatSound1,
  chat: chatSound1,
  team: whistleSound1,
  match: whistleSound2,
  event: whistleSound1,
  system: chatSound2,
  default: chatSound1
};



// Pre-load audio objects for better performance
const audioCache = {};

/**
 * Preload audio files
 */
const preloadAudioFiles = () => {
  Object.entries(NOTIFICATION_SOUNDS).forEach(([type, soundFile]) => {
    try {
      audioCache[type] = new Audio(soundFile);
      audioCache[type].preload = 'auto';
      
      // Add error handling for failed loads
      audioCache[type].addEventListener('error', (error) => {
        console.error(`Failed to preload ${type} sound:`, error);
      });
      
    } catch (error) {
      console.error(`Failed to preload ${type} sound:`, error);
    }
  });
};

// Initialize audio cache
preloadAudioFiles();

/**
 * Play notification sound based on type
 * @param {string} type - Notification type (message, team, match, etc.)
 * @param {number} volume - Volume level (0.0 to 1.0)
 */
export const playNotificationSound = async (type = 'default', volume = 0.7) => {
  try {
    // Check if user preferences allow sounds
    const soundsEnabled = localStorage.getItem('notificationSoundsEnabled');
    
    if (soundsEnabled === 'false') {
      return;
    }

    // Get audio from cache or create new one
    let audio = audioCache[type] || audioCache['default'];
    
    if (!audio) {
      // Fallback: create new audio if not in cache
      const soundFile = NOTIFICATION_SOUNDS[type] || NOTIFICATION_SOUNDS.default;
      audio = new Audio(soundFile);
    } else {
      // Clone the audio to allow multiple simultaneous plays
      audio = audio.cloneNode();
    }
    
    // Set volume
    const finalVolume = Math.max(0, Math.min(1, volume));
    audio.volume = finalVolume;
    
    // Reset audio to beginning
    audio.currentTime = 0;
    
    // Play the sound
    const playPromise = audio.play();
    
    if (playPromise !== undefined) {
      await playPromise
        .then(() => {
          // Sound played successfully
        })
        .catch((error) => {
          // Handle specific browser restrictions
          if (error.name === 'NotAllowedError') {
            // Store failed sound to play later
            localStorage.setItem('pendingNotificationSound', type);
          } else {
            console.error(`Could not play ${type} notification sound:`, error);
          }
        });
    }
    
  } catch (error) {
    console.error('Unexpected error in playNotificationSound:', error);
  }
};

/**
 * Try to play any pending sounds after user interaction
 */
export const playPendingSound = () => {
  const pendingSound = localStorage.getItem('pendingNotificationSound');
  
  if (pendingSound) {
    localStorage.removeItem('pendingNotificationSound');
    playNotificationSound(pendingSound);
  }
};

/**
 * Enable/disable notification sounds
 * @param {boolean} enabled - Whether sounds should be enabled
 */
export const setNotificationSoundsEnabled = (enabled) => {
  localStorage.setItem('notificationSoundsEnabled', enabled.toString());
};

/**
 * Check if notification sounds are enabled
 * @returns {boolean} Whether sounds are enabled
 */
export const areNotificationSoundsEnabled = () => {
  const setting = localStorage.getItem('notificationSoundsEnabled');
  return setting !== 'false'; // Default to enabled if not set
};

/**
 * Test play a sound (useful for settings page)
 * @param {string} type - Sound type to test
 */
export const testNotificationSound = (type = 'default') => {
  playNotificationSound(type, 0.5); // Play at half volume for testing
};

/**
 * Initialize audio context (call after user interaction)
 */
export const initializeAudioContext = () => {
  // Try to create and resume audio context if available
  try {
    if (window.AudioContext || window.webkitAudioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          // AudioContext resumed successfully
        }).catch((error) => {
          console.error('Failed to resume AudioContext:', error);
        });
      }
    }
  } catch (error) {
    console.error('Error creating AudioContext:', error);
  }
  
  // Try to play a silent sound to unlock audio context
  const silentAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmMcBj2V2vLNcysEKOz9/9fK0/7');
  silentAudio.volume = 0.01;
  
  const playPromise = silentAudio.play();
  if (playPromise !== undefined) {
    playPromise
      .then(() => {
        // Audio context unlocked, play any pending sounds
        playPendingSound();
      })
      .catch((error) => {
        // Silent audio play failed, audio context may still be locked
      });
  }
}; 

 